/* eslint-disable no-nested-ternary */
import React from "react";
import FilterItemDndList from "./FilterItemDndList";
import MDBox from "components/Basics/MDBox";
import MDButton from "components/Basics/MDButton";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import MDTypography from "components/Basics/MDTypography";
import { Icon } from "@mui/material";
import { t } from "i18next";
import { useMaterialUIController } from "context";

/**
 * FiltersDndList component
 * This component renders a vertical list of filters that can be reordered
 * via drag-and-drop. Each row exposes a dedicated drag handle so the inner
 * actions (link, delete, presets...) stay clickable.
 * It allows users to add, delete, link, and edit filters.
 * @param {Object} props - The properties for the FiltersDndList component.
 * @param {Array} props.items - The list of filter items to display.
 * @param {Function} props.onChange - Callback function to handle changes in the filter list.
 * @param {Object} props.dictionary - The dictionary object containing filter definitions.
 * @param {Function} props.deleteItem - Function to delete a filter item.
 * @param {Function} props.linkItem - Function to link a filter item.
 * @param {Function} props.removeLink - Function to remove a link from a filter item.
 * @param {Function} props.onEdit - Function to handle editing a filter item.
 * @param {Function} props.onAddFilter - Callback invoked (with the click event) to open the add-filter menu.
 * @returns {JSX.Element} The rendered FiltersDndList component.
 */
const FiltersDndList = ({
	items,
	onChange,
	dictionary = {},
	deleteItem = () => {},
	linkItem = () => {},
	removeLink = () => {},
	onEdit = () => {},
	onAddFilter = () => {}
}) => {
	const [controller] = useMaterialUIController();
	const { darkMode } = controller;

	const moveItem = (fromIndex, toIndex) => {
		const newItems = [...items];
		const [movedItem] = newItems.splice(fromIndex, 1);
		newItems.splice(toIndex, 0, movedItem);
		onChange(newItems);
	};

	const handleDragEnd = result => {
		if (!result.destination) {
			return;
		}

		const sourceIndex = result.source.index;
		const destinationIndex = result.destination.index;

		if (sourceIndex !== destinationIndex) {
			moveItem(sourceIndex, destinationIndex);
		}
	};

	return (
		<DragDropContext onDragEnd={handleDragEnd}>
			<Droppable droppableId="filters" direction="vertical">
				{(provided, snapshot) => (
					<MDBox
						{...provided.droppableProps}
						ref={provided.innerRef}
						display="flex"
						flexDirection="column"
						gap={1.25}
						p={1.5}
						flex="1"
						minHeight="120px"
						sx={({ palette, borders }) => ({
							overflowY: "auto",
							borderRadius: "12px",
							border: `1px dashed ${
								snapshot.isDraggingOver
									? palette.info.main
									: darkMode
										? palette.grey[700]
										: palette.grey[300]
							}`,
							backgroundColor: snapshot.isDraggingOver
								? darkMode
									? "rgba(26,115,232,0.08)"
									: "rgba(26,115,232,0.04)"
								: darkMode
									? "rgba(255,255,255,0.02)"
									: palette.grey[100],
							transition: "background-color 0.2s ease, border-color 0.2s ease"
						})}
					>
						{items.map((item, index) => (
							<Draggable key={item.path} draggableId={item.path} index={index}>
								{(dragProvided, dragSnapshot) => (
									<MDBox
										ref={dragProvided.innerRef}
										{...dragProvided.draggableProps}
										display="flex"
										alignItems="stretch"
										sx={({ palette, boxShadows }) => ({
											borderRadius: "10px",
											overflow: "hidden",
											backgroundColor: darkMode ? palette.background.card : palette.white.main,
											border: `1px solid ${
												dragSnapshot.isDragging
													? palette.info.main
													: darkMode
														? palette.grey[700]
														: palette.grey[300]
											}`,
											boxShadow: dragSnapshot.isDragging
												? boxShadows.lg
												: darkMode
													? "none"
													: boxShadows.xs,
											transition: "box-shadow 0.2s ease, border-color 0.2s ease",
											"&:hover": {
												borderColor: palette.info.main
											}
										})}
									>
										{/* Drag handle */}
										<MDBox
											{...dragProvided.dragHandleProps}
											display="flex"
											alignItems="center"
											justifyContent="center"
											px={0.5}
											sx={({ palette }) => ({
												color: palette.grey[500],
												cursor: "grab",
												backgroundColor: darkMode ? "rgba(255,255,255,0.03)" : palette.grey[100],
												"&:active": { cursor: "grabbing" },
												"&:hover": { color: palette.info.main }
											})}
										>
											<Icon>drag_indicator</Icon>
										</MDBox>

										{/* Filter content */}
										<MDBox flex="1" p={1.5} minWidth={0}>
											<FilterItemDndList
												item={item}
												index={index}
												dictionary={dictionary}
												deleteItem={deleteItem}
												linkItem={linkItem}
												removeLink={removeLink}
												onEdit={onEdit}
											/>
										</MDBox>
									</MDBox>
								)}
							</Draggable>
						))}
						{provided.placeholder}

						{items.length === 0 && (
							<MDBox
								display="flex"
								flexDirection="column"
								alignItems="center"
								justifyContent="center"
								flex="1"
								py={6}
								color="text.secondary"
							>
								<Icon fontSize="large" sx={{ opacity: 0.4, mb: 1 }}>
									filter_alt_off
								</Icon>
								<MDTypography variant="body2" color="text" textAlign="center" mb={2}>
									{t("FILTERS.noFilterDefined")}
								</MDTypography>
								<MDButton variant="gradient" color="info" size="small" onClick={onAddFilter}>
									<Icon>add</Icon>&nbsp;{t("SETTINGS.add")}
								</MDButton>
							</MDBox>
						)}
					</MDBox>
				)}
			</Droppable>
		</DragDropContext>
	);
};

export default FiltersDndList;
